/**
 * ErrataPairView — side-by-side diff of a card and its errata sibling.
 *
 * Renders the corrected vs errata variants of a card so admins (in
 * AdminCardEditModal) and collectors (in CardDetailModal) can see what
 * changed when an errata correction was published. Fields that differ are
 * highlighted with a yellow background; the "before" value is rendered with
 * strikethrough and the "after" value is bold.
 *
 * Source endpoint: GET /api/cards/:id/errata-pair (auth-only).
 *
 * Used by:
 *   - src/components/admin/AdminCardEditModal.tsx (when is_errata is checked
 *     and an errata_of target is set, an inline preview appears)
 *   - src/components/collection/CardDetailModal.tsx (a "Show errata history"
 *     button reveals the diff for cards with errata data)
 */
import { useEffect, useState } from 'react'
import { errataApi } from '@/services/api'
import { proxyImageUrl } from '@/utils/imageLoader'
import { AlertTriangle, Loader2 } from 'lucide-react'

interface Props {
  /** Either side of the pair — the endpoint resolves the partner. */
  cardId: string
  /**
   * Optional pre-loaded pair. When provided we skip the network fetch and
   * render directly. Useful when the parent already holds the pair.
   */
  initialPair?: {
    card: any | null
    corrected: any | null
    errata: any | null
  }
  /** Compact view drops the header row to fit inside narrow drawers. */
  compact?: boolean
}

/**
 * The list of fields we diff. Order matters — we render rows in this order.
 * Each row shows label + the corrected value + the errata value, with a diff
 * highlight when they differ.
 */
const DIFF_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'effect', label: 'Effect' },
  { key: 'trigger', label: 'Trigger' },
  { key: 'rarity', label: 'Rarity' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'cost', label: 'Cost' },
  { key: 'power', label: 'Power' },
  { key: 'counter', label: 'Counter' },
  { key: 'life', label: 'Life' },
]

function asCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

/**
 * Returns true when the two values differ for diff-highlight purposes. Treats
 * null/undefined/empty-string as equivalent so missing data on one side
 * doesn't always look like a change.
 */
function differs(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v))
  return norm(a) !== norm(b)
}

export default function ErrataPairView({ cardId, initialPair, compact }: Props) {
  const [pair, setPair] = useState<Props['initialPair']>(initialPair ?? undefined)
  const [loading, setLoading] = useState(!initialPair)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (initialPair) {
      setPair(initialPair)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    errataApi
      .getPair(cardId)
      .then((data) => {
        if (cancelled) return
        setPair(data)
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load errata pair')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [cardId, initialPair])

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 p-3 text-sm text-gray-500 dark:text-gray-400"
        data-testid="errata-pair-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Loading errata pair…
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="flex items-start gap-2 p-3 rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm"
        data-testid="errata-pair-error"
      >
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>{error}</span>
      </div>
    )
  }

  const corrected = pair?.corrected
  const errata = pair?.errata

  if (!corrected && !errata) {
    return (
      <div
        className="p-3 text-xs text-gray-500 dark:text-gray-400"
        data-testid="errata-pair-empty"
      >
        No errata pair on file for this card.
      </div>
    )
  }

  // Use whichever side carries the explanation/timeline metadata.
  const meta = errata?.errata_published_at || errata?.errata_reason ? errata : corrected

  return (
    <div
      className="border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/40 dark:bg-amber-900/10 p-3 space-y-3"
      data-testid="errata-pair-view"
    >
      {!compact && (
        <header className="flex items-start justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Errata pair
            </h4>
            {meta?.errata_published_at && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300/80">
                Published {meta.errata_published_at}
              </p>
            )}
          </div>
        </header>
      )}

      <div className="grid grid-cols-2 gap-3">
        <CardHeader card={corrected} label="Corrected (current)" />
        <CardHeader card={errata} label="Errata variant" />
      </div>

      <table className="w-full text-xs" data-testid="errata-pair-diff-table">
        <thead>
          <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-amber-200 dark:border-amber-800">
            <th className="font-medium py-1 pr-2">Field</th>
            <th className="font-medium py-1 pr-2">Corrected</th>
            <th className="font-medium py-1">Errata</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amber-100 dark:divide-amber-900/40">
          {DIFF_FIELDS.map((f) => {
            const correctedValue = corrected?.[f.key]
            const errataValue = errata?.[f.key]
            const isDiff = differs(correctedValue, errataValue)
            return (
              <tr
                key={f.key}
                className={
                  isDiff
                    ? 'bg-yellow-100 dark:bg-yellow-900/30'
                    : ''
                }
                data-testid={`errata-pair-row-${f.key}`}
                data-diff={isDiff ? 'yes' : 'no'}
              >
                <td className="py-1 pr-2 font-medium text-gray-700 dark:text-gray-300 align-top">
                  {f.label}
                </td>
                <td className="py-1 pr-2 align-top">
                  {isDiff ? (
                    <span className="line-through text-gray-500 dark:text-gray-400">
                      {asCell(correctedValue)}
                    </span>
                  ) : (
                    <span className="text-gray-700 dark:text-gray-300">
                      {asCell(correctedValue)}
                    </span>
                  )}
                </td>
                <td className="py-1 align-top">
                  {isDiff ? (
                    <span className="font-bold text-amber-900 dark:text-amber-100">
                      {asCell(errataValue)}
                    </span>
                  ) : (
                    <span className="text-gray-700 dark:text-gray-300">
                      {asCell(errataValue)}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {meta?.errata_reason && (
        <p
          className="text-xs text-amber-900 dark:text-amber-200 italic"
          data-testid="errata-pair-reason"
        >
          “{meta.errata_reason}”
        </p>
      )}
    </div>
  )
}

function CardHeader({ card, label }: { card: any | null; label: string }) {
  if (!card) {
    return (
      <div className="rounded border border-dashed border-amber-300 dark:border-amber-700 p-2 text-xs text-gray-500 dark:text-gray-400">
        <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">{label}</div>
        Not present.
      </div>
    )
  }
  return (
    <div className="rounded border border-amber-200 dark:border-amber-800 bg-white dark:bg-dark-surface p-2">
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{label}</div>
      <div className="flex gap-2">
        {card.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={proxyImageUrl(card.image_url)}
            alt={card.name ?? card.id}
            className="w-12 rounded shadow flex-shrink-0"
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement
              img.style.display = 'none'
            }}
          />
        ) : (
          <div className="w-12 aspect-[3/4] flex items-center justify-center bg-gray-100 dark:bg-dark-border text-[10px] text-gray-400 rounded flex-shrink-0">
            no img
          </div>
        )}
        <div className="min-w-0 text-[11px]">
          <div className="font-mono font-semibold text-gray-800 dark:text-dark-text truncate">
            {card.id}
          </div>
          <div className="text-gray-700 dark:text-gray-300 truncate">{card.name ?? '—'}</div>
          <div className="text-gray-500 dark:text-gray-400">
            {card.set_id ?? '—'} · {card.rarity ?? '—'}
          </div>
        </div>
      </div>
    </div>
  )
}
